import Vue from 'vue'
import { get, post } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import * as PN from '@/utils/PermissionNames'

export default {
  namespaced: true,
  state: {
    scimSettings: {
      scim_enabled: false,
      scim_token: '',
      scim_token_updated_at: '',
      scim_employees_sync: false,
      scim_desk_field_mapping: '',
      scim_synced_at: null
    },
    samlSettings: {
      saml_enabled: false,
      saml_entity_id: '',
      saml_x509_cert: '',
      saml_sso_url: '',
      saml_slo_url: ''
    },
    generalSettings: {
      general_map_tooltip_default_display: false,
      general_map_points_zoom_scaling: true,
      general_map_points_zoom_scaling_factor: 1,
      general_max_days_desk_booking_limit: 180
    },
    generalSettingsLoaded: false,
    auditSettings: {
      audit_enabled: false,
      audit_email: '',
      audit_email_status: 'unknown',
      audit_email_code_sent_at: null
    },
    bookingSettings: {
      booking_confirmation_enabled: false,
      booking_confirmation_hours_before: 24,
      booking_autodelete_enabled: false,
      booking_autodelete_hours: 4
    }
  },
  getters: {
    scimUsersSyncEnabled(state) {
      return state.scimSettings.scim_enabled
    },
    scimEmployeesSyncEnabled(state) {
      return state.scimSettings.scim_enabled && state.scimSettings.scim_employees_sync
    },
    scimDeskFieldMappingEnabled(state) {
      return Boolean(state.scimSettings.scim_enabled && 
        state.scimSettings.scim_employees_sync && 
        state.scimSettings.scim_desk_field_mapping)
    },
    canControl(state, getters, rootState, rootGetters) {
      return rootGetters['rbac/can'](PN.SETTINGS_CRUD)
    },
    samlEnabled(state) {
      return state.samlSettings.saml_enabled
    }
  },
  actions: {
    loadAll({ dispatch }) {
      dispatch('loadScimSettings')
      dispatch('loadSamlSettings')
      dispatch('loadGeneralSettings')
    },
    async loadScimSettings({ rootState, commit }) {
      if (rootState.auth.user.scim_sync_enabled) {
        const response = await get(UrlStore.SCIM_SETTINGS)
        commit('setScimSettings', response.data)
      }
    },
    async loadSamlSettings({ commit }) {
      const response = await get(UrlStore.SAML_SETTINGS)
      commit('setSamlSettings', response.data)
    },
    async saveScimSettings({ commit }, settings) {
      const response = await post(UrlStore.SCIM_SETTINGS, settings)
      commit('setScimSettings', response.data)
    },
    async saveSamlSettings({ commit }, settings) {
      const response = await post(UrlStore.SAML_SETTINGS, settings)
      commit('setSamlSettings', response.data)
    },
    async loadGeneralSettings({ commit }) {
      const response = await get(UrlStore.GENERAL_SETTINGS)
      commit('setGeneralSettings', response.data)
      commit('setGeneralSettingsLoaded', true)
    },
    async saveGeneralSettings({ commit }, settings) {
      const response = await post(UrlStore.GENERAL_SETTINGS, settings)
      commit('setGeneralSettings', response.data)
    },
    async loadAuditSettings({ commit }) {
      const response = await get(UrlStore.AUDIT_SETTINGS)
      commit('setAuditSettings', response.data)
    },
    async saveAuditSettings({ commit }, settings) {
      const response = await post(UrlStore.AUDIT_SETTINGS, settings)
      commit('setAuditSettings', response.data)
    },
    async sendAuditConfirmation({ commit }) {
      const response = await post(UrlStore.AUDIT_SEND_CONFIRMATION)
      commit('setAuditSettings', response.data)
    },
    async confirmAuditEmail({ commit }, { code }) {
      const response = await post(UrlStore.AUDIT_CONFIRM_EMAIL, { code })
      commit('setAuditSettings', response.data)
    },
    async loadBookingSettings({ commit }) {
      const response = await get(UrlStore.BOOKING_SETTINGS)
      commit('setBookingSettings', response.data)
    },
    async saveBookingSettings({ commit }, settings) {
      const response = await post(UrlStore.BOOKING_SETTINGS, settings)
      commit('setBookingSettings', response.data)
    }
  },
  mutations: {
    setScimSettings(state, payload) {
      Vue.set(state, 'scimSettings', { ...state.scimSettings, ...payload })
    },
    setSamlSettings(state, payload) {
      Vue.set(state, 'samlSettings', { ...state.samlSettings, ...payload })
    },
    setGeneralSettings(state, payload) {
      Vue.set(state, 'generalSettings', { ...state.generalSettings, ...payload })
    },
    setGeneralSettingsLoaded(state, value) {
      state.generalSettingsLoaded = value
    },
    setAuditSettings(state, payload) {
      Vue.set(state, 'auditSettings', { ...state.auditSettings, ...payload })
    },
    setBookingSettings(state, payload) {
      Vue.set(state, 'bookingSettings', { ...state.bookingSettings, ...payload })
    }
  }
}
